// The signature in an email body lives in a container the app owns: <div data-signature="true">...</div>. It is swapped by that marker, never by matching the
// signature's text, because the rich-text editor rewrites saved HTML (drops styles, adds tbody, flattens tables) and text matching then silently misses.

const SELECTOR = 'div[data-signature]';
const BLANK = '<p><br></p><p><br></p>';

export const markSignature = (sig) => (sig && String(sig).trim() ? `<div data-signature="true">${sig}</div>` : '');

const parse = (html) => {
  if (typeof DOMParser === 'undefined') return null;
  try { return new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html'); } catch (_) { return null; }
};

const isEmptyBody = (b) => !b || String(b).replace(/<(?!img\b)[^>]+>/gi, '').replace(/&nbsp;/g, ' ').trim() === '';

// Existing drafts carry a signature with no marker. It runs from the sender's name line to the end of the body (the name line is the last top-level block
// that mentions the name, or one of the known signatures' first line).
function findUnmarkedStart(doc, names) {
  const kids = Array.from(doc.body.childNodes);
  for (let i = kids.length - 1; i >= 0; i -= 1) {
    const text = (kids[i].textContent || '').replace(/\s+/g, ' ');
    if (names.some((n) => n && text.includes(n))) return i;
  }
  return -1;
}

export const signatureNames = (known = []) => {
  const names = new Set(['Shane Deason']);
  known.forEach((k) => {
    const d = parse(k || '');
    const first = d && (d.body.textContent || '').trim().split(/\s{2,}|\n/)[0];
    if (first && first.length < 40) names.add(first.trim());
  });
  return Array.from(names);
};

// Put `sig` in the body in place of whatever signature is there. One signature block, always: replaced, never appended twice. No stored signature (`sig` empty)
// removes the block. A body with no signature at all (the person deleted it by hand) is left alone.
export function applySignature(body, sig, known = []) {
  const marked = markSignature(sig);
  if (isEmptyBody(body)) return marked ? BLANK + marked : '';
  const doc = parse(body);
  if (!doc) return body;
  const blocks = Array.from(doc.body.querySelectorAll(SELECTOR));
  if (blocks.length) {
    const [first, ...rest] = blocks;
    rest.forEach((b) => b.remove());
    if (!marked) first.remove();
    else { const h = doc.createElement('div'); h.innerHTML = marked; first.replaceWith(h.firstChild); }
    return doc.body.innerHTML;
  }
  const at = findUnmarkedStart(doc, signatureNames([sig, ...known]));
  if (at < 0) return body;
  const kids = Array.from(doc.body.childNodes);
  const h = doc.createElement('div');
  h.innerHTML = marked;
  kids.slice(at + 1).forEach((n) => n.remove());
  if (marked) kids[at].replaceWith(h.firstChild); else kids[at].remove();
  return doc.body.innerHTML;
}

// The body without its signature (to tell whether the person wrote anything besides the signature).
export function withoutSignatureBlock(body) {
  const doc = parse(body || '');
  if (!doc) return String(body || '');
  doc.body.querySelectorAll(SELECTOR).forEach((b) => b.remove());
  return doc.body.innerHTML;
}
